import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, Chip, IconButton, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem, Tooltip,
  TablePagination, Switch,
} from '@mui/material';
import { Search, Add, Edit, Delete, Refresh, Visibility, PhotoCamera } from '@mui/icons-material';
import { InvokeApi, apiList, getCachedDepartments, getCachedSessions } from '../../services/ApiService';
import { Config } from '../../config/Config';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const emptyForm = {
  fullName: '', phone: '', email: '', gender: 'Male', department: '',
  designation: '', age: '', aadhar: '', address_line1: '', address_line2: '',
  city: '', state: '', pincode: '', session: '', join_date: '', resign_date: '',
  status: 'Active', profile_pic: '', created_by: '1',
};

export default function Employees() {
  const { user } = useAuth();
  const isHR = user?.role === 'HR';
  const hrDept = user?.department || '';

  const [employees, setEmployees] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deptFilter, setDeptFilter] = useState(isHR ? hrDept : 'All');
  const [departments, setDepartments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showInactive, setShowInactive] = useState(false);
  const [showResigned, setShowResigned] = useState(false);
  const navigate = useNavigate();
  const hrdepts = hrDept.split(',');
  const loadDepartments = async () => {
    const depts = await getCachedDepartments(Config.main_url);
    if (depts.length) setDepartments(depts);
    return depts;
  };

  const loadSessions = async () => {
    const sessions = await getCachedSessions(Config.main_url);
    if (sessions.length) setSessions(sessions);
  };

  const load = async (deptOverride, inactiveOverride, resignedOverride, deptsOverride) => {
    setLoading(true);
    setPage(0);

    const effectiveDept   = deptOverride   ?? deptFilter;
    const includeInactive = inactiveOverride ?? showInactive;
    const includeResigned = resignedOverride ?? showResigned;

    const fetchByDept = async (dept, status) => {
      const data = await InvokeApi(Config.main_url + apiList.getEmployeesByDepartment, { department: dept, status });
      return data?.employee || [];
    };

    let activeList   = [];
    let inactiveList = [];
    let resignedList = [];

    if (effectiveDept === 'All') {
      let depts = deptsOverride ?? departments;
      if (!depts.length) depts = await loadDepartments();

      const statusesToFetch = ['Active'];
      if (includeInactive) statusesToFetch.push('Inactive');
      if (includeResigned) statusesToFetch.push('Resigned');

      const results = await Promise.all(
        statusesToFetch.map(status =>
          Promise.all(depts.map(d => fetchByDept(d.department, status)))
        )
      );

      results[0].forEach(r => { activeList = [...activeList, ...r]; });
      if (includeInactive) results[1].forEach(r => { inactiveList = [...inactiveList, ...r]; });
      if (includeResigned) results[includeInactive ? 2 : 1].forEach(r => { resignedList = [...resignedList, ...r]; });

    } else {
      const statusesToFetch = ['Active'];
      if (includeInactive) statusesToFetch.push('Inactive');
      if (includeResigned) statusesToFetch.push('Resigned');

      const results = await Promise.all(statusesToFetch.map(s => fetchByDept(effectiveDept, s)));
      activeList   = results[0] || [];
      if (includeInactive) inactiveList = results[1] || [];
      if (includeResigned) resignedList = results[includeInactive ? 2 : 1] || [];
    }

    let allList = [...activeList];
    if (includeInactive) allList = [...allList, ...inactiveList];
    if (includeResigned)  allList = [...allList, ...resignedList];
    setEmployees(allList);
    setFiltered(allList);
    setLoading(false);
  };

  useEffect(() => {
    const init = async () => {
      const [depts] = await Promise.all([loadDepartments(), loadSessions()]);
      load(undefined, undefined, undefined, depts);
    };
    init();
  }, []);

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (!mounted) { setMounted(true); return; } 
    load();
  }, [deptFilter, showInactive, showResigned]);

  useEffect(() => {
    const q = search.toLowerCase();
    const result = employees.filter(e =>
      String(e.ID).includes(q) ||
      e.name?.toLowerCase().includes(q) ||
      e.phone?.includes(q) ||
      e.department?.toLowerCase().includes(q) ||
      e.email?.toLowerCase().includes(q)
    );
    setFiltered(result);
    setPage(0);
  }, [search, employees]);

  const openAdd = () => { setForm(emptyForm); setEditMode(false); setPreviewUrl(null); setDialog(true); };
  const openEdit = (emp) => {
    const picPath = emp.profile_pic_path || (emp.profile_pic ? emp.profile_pic.split('/').pop() : '');
    setForm({
      employeeId: emp.ID, fullName: emp.name, phone: emp.phone, email: emp.email,
      gender: emp.gender || 'Male', department: emp.department, designation: emp.designation,
      age: emp.age, aadhar: emp.aadhar, address_line1: emp.address_line1,
      address_line2: emp.address_line2, city: emp.city, state: emp.state,
      pincode: emp.pincode, session: emp.session, join_date: emp.join_date,
      resign_date: emp.resign_date, status: emp.status,
      profile_pic: picPath,
      password: emp.password, created_by: '1',
    });
    setPreviewUrl(emp.profile_pic || null);
    setEditMode(true);
    setDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Mark this employee as Resigned?')) return;
    const data = await InvokeApi(Config.main_url + apiList.deleteEmployee, { id });
    if (data?.code === '200') load();
    else alert(data?.message || 'Failed');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPreviewUrl(ev.target.result);
    reader.readAsDataURL(file);
    setUploading(true);
    try {
      const phpForm = new FormData();
      phpForm.append('profile_pic', file);
      const phpData = await postImg(Config.main_url + apiList.addEmployeeProfileImage, phpForm);

      if (phpData?.code === '200' || phpData?.code === 200) {
        setForm(p => ({ ...p, profile_pic: phpData.imageName }));
        alert('Profile photo uploaded! Click Update to save.');
      } else {
        alert('PHP upload failed: ' + (phpData?.message || 'Unknown error'));
        return;
      }

      try {
        const pyForm = new FormData();
        pyForm.append('emp_id', form.employeeId);
        pyForm.append('profile_image', file);
        const pyData = await postImg(Config.mainpy_url + apiList.AddProfileImgStdPy, pyForm);
        if (pyData?.code === '200') {
          // alert('Face recognition updated successfully!');
        } else {
          // alert('Update failed: ' + (pyData?.message || 'Unknown error'));
        }
      } catch (pyErr) {
        // alert('Face recognition update failed: ' + pyErr.message);
      }

    } catch (err) {
      console.error(err);
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.fullName || !form.phone || !form.department) {
      alert('Full name, phone and department are required');
      return;
    }
    setSaving(true);
    const url = editMode ? apiList.editEmployee : apiList.addEmployee;
    const payload = { ...form };
    if (editMode && !payload.profile_pic) {
      delete payload.profile_pic;
    }
    const data = await InvokeApi(Config.main_url + url, payload);
    setSaving(false);
    if (data?.code === '200') { setDialog(false); load(); }
    else alert(data?.message || 'Failed to save');
  };

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Employees</Typography>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} employee{filtered.length !== 1 ? 's' : ''} found
            {isHR && hrDept && <span style={{ marginLeft: 8, color: '#880e4f', fontWeight: 700 }}>· {hrDept}</span>}
          </Typography>        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Show Inactive toggle */}
          {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid #e8eaf6', borderRadius: 2, px: 1.5, py: 0.5 }}>
            <Typography fontSize={13} fontWeight={600} color="text.secondary">Inactive</Typography>
            <Switch
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#1a237e' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1a237e' },
              }}
            />
          </Box> */}
          {/* Show Resigned toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid #e8eaf6', borderRadius: 2, px: 1.5, py: 0.5 }}>
            <Typography fontSize={13} fontWeight={600} color="text.secondary">Resigned</Typography>
            <Switch
              checked={showResigned}
              onChange={e => { setShowResigned(e.target.checked); load(undefined, undefined, e.target.checked); }}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#c62828' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#c62828' },
              }}
            />
          </Box>
          {/* HR cannot add employees */}
          {/* {!isHR && ( */}
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Add Employee 
          </Button>
          {/* )} */}
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search by ID, name, phone, email, dept..." value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
          {/* HR sees only their dept — no dropdown */}
          {isHR ? (
            // {/* <Chip label={hrDept} sx={{ background: '#fce4ec', color: '#880e4f', fontWeight: 700 }} /> */}
            <TextField
              select
              size="small"
              label="Department"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {/* <MenuItem value="All">All Departments</MenuItem> */}

              {hrdepts.map((d, index) => (
                <MenuItem key={index} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField select size="small" label="Department" value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="All">All Departments </MenuItem>
              {departments.map(d => <MenuItem key={d.ID} value={d.department}>{d.department}</MenuItem>)}
            </TextField>
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={() => load()} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    {['Emp ID', 'Employee', 'Phone', 'Department', 'Designation', 'Status', 'Actions'].map(h => (<TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        No employees found
                      </TableCell>
                    </TableRow>
                  ) : paginated.map((emp) => (
                    <TableRow key={emp.ID} hover
                      sx={{ opacity: emp.status === 'Resigned' ? 0.65 : emp.status === 'Inactive' ? 0.75 : 1 }}>
                      <TableCell>
                        <Chip label={`#${emp.ID}`} size="small"
                          sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 12 }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar src={emp.profile_pic}
                            sx={{ width: 36, height: 36, background: 'linear-gradient(135deg,#1a237e,#1565c0)', color: '#fff', fontWeight: 700, fontSize: 14 }}>
                            {emp.name?.[0]}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={600} fontSize={13}>{emp.name}</Typography>
                            <Typography fontSize={11} color="text.secondary">{emp.email || '—'}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{emp.phone || '—'}</TableCell>
                      <TableCell>
                        <Chip label={emp.department} size="small"
                          sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{emp.designation || '—'}</TableCell>
                      <TableCell>
                        <Chip label={emp.status} size="small"
                          sx={{
                            background: emp.status === 'Active' ? '#e8f5e9' : emp.status === 'Resigned' ? '#fff3e0' : '#ffebee',
                            color: emp.status === 'Active' ? '#2e7d32' : emp.status === 'Resigned' ? '#e65100' : '#c62828',
                            fontWeight: 600,
                          }} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Profile">
                          <IconButton size="small" onClick={() => navigate(`/employees/${emp.ID}`)} sx={{ color: '#1a237e' }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(emp)} sx={{ color: '#1565c0' }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Resign">
                          <IconButton size="small" onClick={() => handleDelete(emp.ID)} sx={{ color: '#c62828' }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }}
            />
          </>
        )}
      </Card>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          {editMode ? `Edit Employee #${form.employeeId}` : 'Add New Employee'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {editMode && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar src={previewUrl} sx={{
                  width: 80, height: 80, border: '3px solid #e8eaf6',
                  background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                  fontSize: 28, fontWeight: 900, color: '#fff',
                }}>
                  {form.fullName?.[0]}
                </Avatar>
                <label htmlFor="popup-profile-upload">
                  <input id="popup-profile-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
                  <Tooltip title="Change Photo">
                    <IconButton component="span" size="small" disabled={uploading} sx={{
                      position: 'absolute', bottom: -2, right: -2,
                      background: '#1a237e', color: '#fff', width: 26, height: 26,
                      '&:hover': { background: '#1565c0' },
                    }}>
                      {uploading ? <CircularProgress size={12} color="inherit" /> : <PhotoCamera sx={{ fontSize: 14 }} />}
                    </IconButton>
                  </Tooltip>
                </label>
              </Box>
            </Box>
          )}
          <Grid container spacing={2}>
            {[
              { label: 'Full Name *', key: 'fullName' },
              { label: 'Phone *', key: 'phone' },
              { label: 'Email', key: 'email' },
              { label: 'Aadhar', key: 'aadhar' },
              { label: 'Age', key: 'age' },
              { label: 'Designation', key: 'designation' },
              { label: 'Address Line 1', key: 'address_line1' },
              { label: 'Address Line 2', key: 'address_line2' },
              { label: 'City', key: 'city' },
              { label: 'State', key: 'state' },
              { label: 'Pincode', key: 'pincode' },
              { label: 'Join Date', key: 'join_date', type: 'date' },
            ].map(({ label, key, type }) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField fullWidth size="small" label={label} type={type || 'text'}
                  value={form[key] || ''} onChange={f(key)}
                  InputLabelProps={type === 'date' ? { shrink: true } : {}} />
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Gender" value={form.gender} onChange={f('gender')}>
                {['Male', 'Female', 'Other'].map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Department *" value={form.department} onChange={f('department')}>
                {departments.map(d => <MenuItem key={d.ID} value={d.department}>{d.department}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Session" value={form.session || ''} onChange={f('session')}>
                <MenuItem value="">— None —</MenuItem>
                {sessions.map(s => <MenuItem key={s.ID} value={s.session}>{s.session} ({s.check_in_time} – {s.check_out_time})</MenuItem>)}
              </TextField>
            </Grid>
            {editMode && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Password" value={form.password || ''} onChange={f('password')} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, minWidth: 100 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (editMode ? 'Update' : 'Add Employee')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
